import { Button, Input, NativeSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router";
import {
	Async,
	Badge,
	Empty,
	Field,
	KV,
	PageHead,
	Pager,
	SearchBox,
	Segmented,
	Status,
	useAction,
	useConfirm,
} from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Org, type OrgDetail, type Plan } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync, useDebounced, usePaged } from "@/lib/hooks";

export function OrgsPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [filter, setFilter] = useState<"" | "paid" | "paused" | "expired">("");
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.orgs({ q: query, filter, limit, offset }),
		(page) => page.orgs,
		[query, filter],
	);

	return (
		<>
			<PageHead title={t("orgs.title")} lede={t("orgs.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<Segmented
					value={filter}
					onChange={setFilter}
					options={[
						{ value: "", label: t("state.all") },
						{ value: "paid", label: t("orgs.filter.paid") },
						{ value: "paused", label: t("orgs.filter.paused") },
						{ value: "expired", label: t("orgs.filter.expired") },
					]}
				/>
			</div>
			<Async state={state}>
				{({ total }) =>
					rows.length === 0 ? (
						<Empty />
					) : (
						<>
							<div className="table-scroll">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("orgs.name")}</TableHead>
											<TableHead>{t("orgs.plan")}</TableHead>
											<TableHead>{t("orgs.subscription")}</TableHead>
											<TableHead className="num">{t("orgs.credits")}</TableHead>
											<TableHead className="num">{t("orgs.entities")}</TableHead>
											<TableHead className="num">{t("orgs.members")}</TableHead>
											<TableHead className="num">{t("orgs.spent30")}</TableHead>
											<TableHead>{t("state.created")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((o: Org) => (
											<TableRow key={o.id}>
												<TableCell>
													<Link to={`/orgs/${o.id}`}>{o.name}</Link>
													{o.paused ? (
														<>
															{" "}
															<Badge tone="warning">{t("orgs.paused")}</Badge>
														</>
													) : null}
													<span className="sub mono">{o.slug}</span>
												</TableCell>
												<TableCell>{o.plan_key}</TableCell>
												<TableCell>
													{o.subscription_status ? (
														<Status value={o.subscription_status} prefix="billing.status" />
													) : (
														"—"
													)}
													{o.subscription_ends_at ? (
														<span className="sub">{fmt.date(locale, o.subscription_ends_at)}</span>
													) : null}
												</TableCell>
												<TableCell className="num">{fmt.number(locale, o.credits)}</TableCell>
												<TableCell className="num">{o.entities}</TableCell>
												<TableCell className="num">{o.members}</TableCell>
												<TableCell className="num">{fmt.usd(locale, o.spent_30d_micros)}</TableCell>
												<TableCell>{fmt.date(locale, o.created_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
							<Pager shown={rows.length} total={total} hasMore={hasMore} busy={loadingMore} onMore={more} />
						</>
					)
				}
			</Async>
		</>
	);
}

export function OrgPage() {
	const { id = "" } = useParams();
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<OrgDetail>(() => api.org(id), [id]);
	const plans = useAsync(() => api.plans(), []);
	const confirm = useConfirm();
	const { run, busy } = useAction();

	return (
		<Async state={state}>
			{(d) => {
				const o = d.org;
				return (
					<>
						<PageHead
							title={o.name}
							lede={
								<span className="chips">
									<Status value={d.subscription_state} prefix="orgs.state" />
									{o.paused ? <Badge tone="warning">{t("orgs.paused")}</Badge> : null}
									<Badge tone="outline">{o.plan_key}</Badge>
									<Badge tone="outline">{t(o.onboarded_at ? "orgs.onboarded" : "orgs.notOnboarded")}</Badge>
								</span>
							}
							crumbs={[{ to: "/orgs", label: t("orgs.title") }]}
						>
							<Button
								variant={o.paused ? "default" : "destructive"}
								type="button"
								disabled={busy}
								onClick={async () => {
									const ok = await confirm(t(o.paused ? "orgs.resume" : "orgs.pause"), t("orgs.pauseHint"), !o.paused);
									if (ok)
										run(
											() => api.pauseOrg(o.id, !o.paused),
											() => t("state.saved"),
										).then(state.reload);
								}}
							>
								{t(o.paused ? "orgs.resume" : "orgs.pause")}
							</Button>
						</PageHead>

						<div className="stats" style={{ marginBottom: "var(--s-8)" }}>
							<div className="stat">
								<span className="label">{t("orgs.balance")}</span>
								<span className="value">{fmt.number(locale, d.credits.balance)}</span>
								<span className="foot">
									{t("orgs.permanent", { n: fmt.number(locale, d.credits.permanent) })}
									{d.credits.expiring > 0
										? ` · ${t("orgs.expiring", { n: fmt.number(locale, d.credits.expiring), date: fmt.date(locale, d.credits.expires_at) })}`
										: ""}
								</span>
							</div>
							<div className="stat">
								<span className="label">{t("orgs.spent30")}</span>
								<span className="value">{fmt.usd(locale, o.spent_30d_micros)}</span>
							</div>
							<div className="stat">
								<span className="label">{t("orgs.entities")}</span>
								<span className="value">{o.entities}</span>
							</div>
							<div className="stat">
								<span className="label">{t("orgs.members")}</span>
								<span className="value">{o.members}</span>
							</div>
						</div>

						<div className="grid-2">
							<div className="card">
								<KV
									items={[
										[t("state.id"), <span className="mono">{o.id}</span>],
										["slug", <span className="mono">{o.slug}</span>],
										[t("orgs.kind"), t(`orgs.kind.${o.kind}`)],
										[t("state.created"), fmt.dateTime(locale, o.created_at)],
									]}
								/>
							</div>

							<SettingsCard org={o} plans={plans.data?.plans ?? []} onSaved={state.reload} />

							<div className="card">
								<h2>{t("orgs.members")}</h2>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("users.email")}</TableHead>
											<TableHead>{t("orgs.role")}</TableHead>
											<TableHead>{t("orgs.since")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{d.members.map((m) => (
											<TableRow key={m.user_id}>
												<TableCell>
													<Link to={`/users/${m.user_id}`}>{m.email}</Link>
													{m.name ? <span className="sub">{m.name}</span> : null}
												</TableCell>
												<TableCell>{m.role}</TableCell>
												<TableCell>{fmt.date(locale, m.since)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>

							<div className="card">
								<h2>{t("orgs.entities")}</h2>
								{d.entities.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>{t("entities.name")}</TableHead>
												<TableHead>{t("state.status")}</TableHead>
												<TableHead className="num">{t("entities.published")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{d.entities.map((e) => (
												<TableRow key={e.id}>
													<TableCell>
														<Link to={`/entities/${e.id}`}>{e.name}</Link>
														{e.paused ? (
															<>
																{" "}
																<Badge tone="warning">{t("entities.paused")}</Badge>
															</>
														) : null}
													</TableCell>
													<TableCell>
														<Status value={e.status} prefix="entities.status" />
													</TableCell>
													<TableCell className="num">{e.published_pages}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>

							<CreditsCard org={o} onDone={state.reload} />
							<SubscriptionCard org={o} detail={d} plans={plans.data?.plans ?? []} onDone={state.reload} />
						</div>

						<div className="section">
							<h2>{t("orgs.ledger")}</h2>
							{d.ledger.length === 0 ? (
								<Empty />
							) : (
								<div className="table-scroll">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>{t("audit.when")}</TableHead>
												<TableHead>{t("orgs.ledgerReason")}</TableHead>
												<TableHead className="num">{t("orgs.ledgerDelta")}</TableHead>
												<TableHead className="num">{t("orgs.ledgerAfter")}</TableHead>
												<TableHead>{t("orgs.ledgerRef")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{d.ledger.map((l) => {
												const key = `ledger.${l.reason}`;
												const label = t(key);
												return (
													<TableRow key={l.id}>
														<TableCell>{fmt.dateTime(locale, l.created_at)}</TableCell>
														<TableCell>{label === key ? l.reason : label}</TableCell>
														<TableCell className="num">{fmt.signed(locale, l.delta)}</TableCell>
														<TableCell className="num">{fmt.number(locale, l.balance_after)}</TableCell>
														<TableCell className="mono faint">{l.ref_id ?? ""}</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</div>
							)}
						</div>

						<div className="grid-2 section">
							<div className="card">
								<h2>{t("orgs.usage")}</h2>
								{d.usage_30d.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>{t("usage.model")}</TableHead>
												<TableHead className="num">{t("usage.calls")}</TableHead>
												<TableHead className="num">{t("usage.billed")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{d.usage_30d.map((u) => (
												<TableRow key={u.model}>
													<TableCell className="mono">{u.model}</TableCell>
													<TableCell className="num">{u.calls}</TableCell>
													<TableCell className="num">{fmt.usd(locale, u.billed_micros)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
							<div className="card">
								<h2>{t("orgs.jobs")}</h2>
								{d.jobs.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table>
										<TableBody>
											{d.jobs.map((j) => (
												<TableRow key={j.id}>
													<TableCell>
														<Link to={`/jobs/${j.id}`} className="mono">
															{j.kind}
														</Link>
													</TableCell>
													<TableCell>
														<Status value={j.status} prefix="jobs.status" />
													</TableCell>
													<TableCell>{fmt.relative(locale, j.created_at)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
							<div className="card">
								<h2>{t("orgs.actions")}</h2>
								{d.actions.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table>
										<TableBody>
											{d.actions.map((a) => (
												<TableRow key={a.id}>
													<TableCell>{a.actor ?? "—"}</TableCell>
													<TableCell className="mono">{a.action}</TableCell>
													<TableCell>{fmt.dateTime(locale, a.created_at)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
						</div>
					</>
				);
			}}
		</Async>
	);
}

function SettingsCard({ org, plans, onSaved }: { org: Org; plans: Plan[]; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const [name, setName] = useState(org.name);
	const [plan, setPlan] = useState(org.plan_key);
	const [kind, setKind] = useState(org.kind);

	async function submit(e: FormEvent) {
		e.preventDefault();
		await run(
			() =>
				api.updateOrg(org.id, {
					name: name !== org.name ? name : undefined,
					plan_key: plan !== org.plan_key ? plan : undefined,
					kind: kind !== org.kind ? kind : undefined,
				}),
			() => t("state.saved"),
		);
		onSaved();
	}

	return (
		<form className="card form" onSubmit={submit}>
			<h2>{t("orgs.settings")}</h2>
			<Field label={t("orgs.name")}>
				<Input value={name} onChange={(e) => setName(e.target.value)} />
			</Field>
			<div className="field-row">
				<Field label={t("orgs.plan")} hint={t("orgs.planHint")}>
					<NativeSelect value={plan} onChange={(e) => setPlan(e.target.value)}>
						{(plans.length ? plans : [{ key: org.plan_key, name_en: org.plan_key, active: true } as Plan]).map((p) => (
							<option key={p.key} value={p.key}>
								{p.key}
								{p.active ? "" : " (inactive)"}
							</option>
						))}
					</NativeSelect>
				</Field>
				<Field label={t("orgs.kind")}>
					<NativeSelect value={kind} onChange={(e) => setKind(e.target.value)}>
						<option value="personal">{t("orgs.kind.personal")}</option>
						<option value="agency">{t("orgs.kind.agency")}</option>
					</NativeSelect>
				</Field>
			</div>
			<div className="form-foot">
				<Button variant="default" type="submit" disabled={busy}>
					{t("state.save")}
				</Button>
			</div>
		</form>
	);
}

function CreditsCard({ org, onDone }: { org: Org; onDone: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const [credits, setCredits] = useState("1000");
	const [expires, setExpires] = useState("");
	const [note, setNote] = useState("");
	const [revoke, setRevoke] = useState("");
	const [revokeNote, setRevokeNote] = useState("");

	async function grant(e: FormEvent) {
		e.preventDefault();
		const result = await run(
			() =>
				api.grant(org.id, {
					credits: Number(credits),
					note,
					expires_at: expires ? fmt.endOfDay(expires) : null,
				}),
			(r) => t("orgs.granted", { balance: fmt.number(locale, r.balance) }),
		);
		if (result) onDone();
	}

	async function takeBack(e: FormEvent) {
		e.preventDefault();
		const ok = await confirm(
			t("orgs.revoke"),
			t("orgs.revokeConfirm", { credits: fmt.number(locale, Number(revoke)), name: org.name }),
			true,
		);
		if (!ok) return;
		const result = await run(
			() => api.revoke(org.id, { credits: Number(revoke), note: revokeNote }),
			(r) => t("orgs.revoked", { balance: fmt.number(locale, r.balance) }),
		);
		if (result) {
			setRevoke("");
			setRevokeNote("");
			onDone();
		}
	}

	return (
		<div className="card stack">
			<form className="form" onSubmit={grant}>
				<h2>{t("orgs.grant")}</h2>
				<p className="hint">{t("orgs.grantHint")}</p>
				<div className="field-row">
					<Field label={t("orgs.grantAmount")}>
						<Input
							type="number"
							min={1}
							step={1}
							required
							value={credits}
							onChange={(e) => setCredits(e.target.value)}
						/>
					</Field>
					<Field label={t("orgs.grantExpires")} hint={t("orgs.grantExpiresHint")}>
						<Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
					</Field>
				</div>
				<Field label={t("state.note")} hint={t("state.noteHint")}>
					<Input required value={note} onChange={(e) => setNote(e.target.value)} />
				</Field>
				<div className="form-foot">
					<Button variant="primary" type="submit" disabled={busy}>
						{t("orgs.grant")}
					</Button>
				</div>
			</form>
			<form className="form" onSubmit={takeBack}>
				<h2>{t("orgs.revoke")}</h2>
				<p className="hint">{t("orgs.revokeHint")}</p>
				<div className="field-row">
					<Field label={t("orgs.grantAmount")}>
						<Input type="number" min={1} step={1} required value={revoke} onChange={(e) => setRevoke(e.target.value)} />
					</Field>
					<Field label={t("state.note")} hint={t("state.noteHint")}>
						<Input required value={revokeNote} onChange={(e) => setRevokeNote(e.target.value)} />
					</Field>
				</div>
				<div className="form-foot">
					<Button variant="destructive" type="submit" disabled={busy || !revoke || !revokeNote}>
						{t("orgs.revoke")}
					</Button>
				</div>
			</form>
		</div>
	);
}

function SubscriptionCard({
	org,
	detail,
	plans,
	onDone,
}: {
	org: Org;
	detail: OrgDetail;
	plans: Plan[];
	onDone: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { run, busy } = useAction();
	const confirm = useConfirm();
	const active = plans.filter((p) => p.active);
	const [plan, setPlan] = useState(org.plan_key);
	const [months, setMonths] = useState("1");
	const [note, setNote] = useState("");
	const live = detail.subscriptions.find((s) => ["active", "past_due", "paused"].includes(s.status));
	const paid = active.find((p) => p.key === plan)?.price_usd_micros ?? 0;

	async function submit(e: FormEvent) {
		e.preventDefault();
		const ok = await confirm(t("orgs.comp"), t("orgs.compHint"));
		if (!ok) return;
		const result = await run(
			() => api.setPlan(org.id, { plan_key: plan, months: Number(months), note: note || undefined }),
			(r) =>
				r.period_end
					? t("orgs.comped", { plan, until: fmt.date(locale, r.period_end), credits: r.credits_granted })
					: t("orgs.planSet", { plan }),
		);
		if (result) onDone();
	}

	return (
		<div className="card stack">
			<div>
				<h2>{t("orgs.subscriptions")}</h2>
				{detail.subscriptions.length === 0 ? (
					<p className="muted">{t("state.none")}</p>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("orgs.plan")}</TableHead>
								<TableHead>{t("state.status")}</TableHead>
								<TableHead>{t("orgs.period")}</TableHead>
								<TableHead>{t("orgs.paygateId")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{detail.subscriptions.map((s) => (
								<TableRow key={s.id}>
									<TableCell>
										{s.plan_key} · {s.interval}
										{s.pending_plan_key ? (
											<span className="sub">{t("orgs.pending", { plan: s.pending_plan_key })}</span>
										) : null}
									</TableCell>
									<TableCell>
										<Status value={s.status} prefix="billing.status" />
									</TableCell>
									<TableCell>
										{fmt.date(locale, s.current_period_start)} → {fmt.date(locale, s.current_period_end)}
									</TableCell>
									<TableCell className="mono faint">{s.paygate_subscription_id ?? "—"}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>
			<form className="form" onSubmit={submit}>
				<h2>{t("orgs.comp")}</h2>
				<p className="hint">{t("orgs.compHint")}</p>
				<div className="field-row">
					<Field label={t("orgs.plan")}>
						<NativeSelect value={plan} onChange={(e) => setPlan(e.target.value)}>
							{active.map((p) => (
								<option key={p.key} value={p.key}>
									{locale === "zh" ? p.name_zh : p.name_en} ·{" "}
									{p.price_usd_micros > 0 ? `${p.monthly_credits}` : t("orgs.state.none")}
								</option>
							))}
						</NativeSelect>
					</Field>
					<Field label={t("orgs.compMonths")}>
						<Input
							type="number"
							min={1}
							max={36}
							value={months}
							disabled={paid <= 0}
							onChange={(e) => setMonths(e.target.value)}
						/>
					</Field>
				</div>
				<Field label={t("state.note")}>
					<Input value={note} onChange={(e) => setNote(e.target.value)} />
				</Field>
				<div className="form-foot">
					<Button
						variant="default"
						type="submit"
						disabled={busy || active.length === 0 || !!live?.paygate_subscription_id}
					>
						{t("orgs.comp")}
					</Button>
					{live && !live.paygate_subscription_id ? (
						<Button
							variant="destructive"
							type="button"
							disabled={busy}
							onClick={async () => {
								const ok = await confirm(t("orgs.end"), t("orgs.endConfirm"), true);
								if (ok) {
									const r = await run(
										() => api.endSubscription(org.id, note || undefined),
										() => t("orgs.ended"),
									);
									if (r) onDone();
								}
							}}
						>
							{t("orgs.end")}
						</Button>
					) : null}
					<span className="hint">{t("orgs.endHint")}</span>
				</div>
			</form>
		</div>
	);
}
